<template>
    <div>
        <div class="card dashcard mb-0">
            <div class="card-body">
                <div class="form-row">
                    <div class="col col-12 col-md-2">
                        <az-input
                            type="date"
                            v-model="referencia">       
                            <template v-slot:button-pre>
                                <div class="input-group-prepend">
                                    <button type="button" class="btn btn-outline-primary" @click="load()" 
                                        :disabled="$store.state.loading" id="refresh-button">
                                        <i class="fas fa-sync-alt fa-fw"></i>
                                    </button>
                                </div>
                            </template>
                        </az-input>
                    </div>

                    <div class="col col-12 col-md-2">
                        <div class="input-group mb-1">
                            <div class="input-group-prepend">
                                <span class="input-group-text" id="basic-addon1">
                                    <i class="fa fa-building fa-fw" aria-hidden="true"></i>
                                </span>
                            </div>
                            <select class="form form-control" v-model="filtro.id_unidade_operacao">
                                <option value="">Unidades...</option>
                                <option v-for="(und, iux) in $util.unidades_com_tags(false, true)" 
                                    :value="und.id_unidade_operacao" :key="iux">{{ und.search }}</option>
                            </select>
                        </div>
                    </div>

                    <div class="col col-12 col-md-4">
                        <az-input
                            icon="fa fa-filter fa-fw"
                            type="select"
                            :optionsList="filas.map(item => { return { id: item.id_fila_laudo, search: item.search } })"
                            optionsData="id"
                            optionsDisplay="search"
                            v-model="filtro.id_fila_laudo"
                            :edicao="!$store.state.loading"
                            @input="load()" />
                    </div>
                    <div class="col col-12 col-md-4">
                        <az-input icon="fas fa-user-md fa-fw" 
                            type="select" 
                            placeholder="Todos os Médicos..."
                            optionsMode="aql"
                            service="medicos.index"
                            optionsName="medicos-cadastro"
                            :optionsParm="{ ativos: true }"
                            optionsData="id_pessoa"
                            v-model="filtro.id_medico" />
                    </div>

                </div>

                <div class="form-row mt-2">
                    <div class="col col-12 col-md-4">
                        <az-input 
                            icon="fa-solid fa-user-injured fa-fw"
                            placeholder="Selecionar paciente..."
                            type="autocomplete" 
                            optionsMode="aql"
                            service="pacientes.index"
                            optionsDisplay="search"
                            ref="paciente"
                            v-model="filtro.paciente" 
                            :optimized_ac="true" />
                    </div>
                    <div class="col col-12" :class="is_fila_intern ? 'col-md-4' : 'col-md-8'">
                        <az-input
                            icon="fa fa-search"
                            v-model="search" />       
                    </div>

                    <div v-if="is_fila_intern" class="col col-12 col-md-4">
                        <az-input 
                            placeholder="Unidade de Internação..."
                            icon="fa-solid fa-bed fa-fw"
                            type="select"
                            optionsMode="aql"
                            :optionsName="'centro_custos.index.leito.' + filtro.id_unidade_operacao"
                            service="centro_custos.index"
                            optionsData="id_centro_custo" 
                            optionsDisplay="st_nome"
                            :optionsParm="{ 
                                ativos: true,
                                id_unidade_operacao: filtro.id_unidade_operacao,
                                leitos: true, 
                            }"
                            v-model="filtro.id_centro_custo"  />
                    </div>
                </div>
            </div>
        </div>

        <div class="progress mt-1 mb-3">
            <div class="indeterminate" v-if="$store.state.loading && dados"></div>
            <div class="determinate" v-else></div>
        </div>

        <az-empty-state v-if="!lista.length" />

        <div class="card carde mb-2 w3-animate-opacity" v-for="(receita, index) in lista" :key="index">
            <div class="card-body p-1 pr-2">
                <div class="row">
                    <div class="col col-12 col-md-2 d-flex flex-column justify-content-center">
                        <button type="button" class="btn btn-sm btn-danger" @click="aprazar (receita)" v-if="receita.fl_suspensa">
                            Suspensa
                            <span class="badge badge-light ml-2">{{ receita.items.length }}</span>
                        </button>
                        <button type="button" class="btn btn-sm btn-success" @click="aprazar (receita)" v-else-if="!aprazados(receita)">
                            Aprazar
                            <span class="badge badge-light ml-2">{{ receita.items.length }}</span>
                        </button>
                        <button type="button" class="btn btn-sm btn-outline-secondary" @click="aprazar (receita)" v-else>
                            Editar
                            <span class="badge badge-light ml-2">{{ receita.items.length }}</span>
                        </button>

                        <div v-if="receita.dt_inclusao" class="small text-muted text-center">
                            {{ $util.getFormatDate(receita.dt_inclusao) }} {{ receita.dt_inclusao.slice(11, 16) }}
                        </div>
                    </div>

                    <!-- Identificação do Paciente -->
                    <div class="col col-12 col-md-4">
                        <celula-dados-paciente :hor="tratar_paciente (receita.anamnese.atendimento_procedimento.atendimento)" />
                    </div>

                    <!-- Identificação do Procedimento -->
                    <div class="col col-12 col-md-4">
                        <!-- Status da Prescrição -->
                        <div v-if="receita.contato">
                            <span class="badge" :class="'badge-' + cor_contato(receita)">
                                {{ receita.contato.contato_status.search }}
                                / {{ quando(receita.contato.created_at) }}
                            </span> 
                        </div>

                        <celula-dados-procedimento :hor="tratar_procedimento (receita)" />
                    </div>

                    <div class="col col-12 col-md-2">
                        <div v-if="receita.internacao && receita.internacao.local" title="Internação / Leito">
                            <div>
                                <span class="small text-muted">
                                    {{ receita.internacao.local.centro_custo.st_nome }}
                                </span>
                            </div>
                            <div>
                                <span class="small">
                                    {{ receita.internacao.local.st_local }}
                                </span>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </div>

        <!-- Modal -->
        <!--
        <div class="modal fade" id="aprazamentoModal" tabindex="-1" role="dialog" aria-labelledby="modelTitleId" aria-hidden="true" data-backdrop="static" data-keyboard="false">
            <div class="modal-dialog modal-xl modal-dialog-scrollable" role="document">
                <div class="modal-content" v-if="item">
                    <div class="modal-header">
                        <h5 class="modal-title">
                            Aprazar: 
                            {{ item.anamnese.atendimento_procedimento.atendimento.paciente.search }}

                            <span class="badge badge-secondary ml-2" 
                                v-if="item.anamnese.atendimento_procedimento.atendimento.marcacao && item.anamnese.atendimento_procedimento.atendimento.marcacao.in_qt_dia">
                                D{{ item.anamnese.atendimento_procedimento.atendimento.marcacao.in_qt_dia }}
                            </span>
                        </h5>
                        <button type="button" class="close" data-dismiss="modal" aria-label="Close">
                            <span aria-hidden="true">&times;</span>
                        </button>
                    </div>
                    <div class="modal-body">
                        <div class="alert alert-danger" v-if="item.fl_suspensa">
                            <i class="fa-solid fa-triangle-exclamation fa-fw mr-3"></i>
                            <strong>Suspensa</strong>
                        </div>

                        <table class="table table-hover">
                            <div v-if="item.in_duracao > 1 && item.anamnese.atendimento_procedimento.atendimento.id_tipo_atendimento == 7" class="alert alert-secondary d-flex justify-content-between">
                                <div>
                                    Prescrito em {{ $util.getFormatDate(item.dt_referencia) }}
                                </div>
                                <div>
                                    Duração: {{ item.in_duracao }} dias.
                                </div>
                            </div>
                                

                            <tr v-for="(medic, imx) in item.items" :key="imx">
                                <div class="row">
                                    <div class="col col-12 col-md-8" :class="medic.block_d ?  'text-muted' : ''">
                                        <div>{{ medic.item.st_medicamento }}</div>
                                        <div class="text-muted">{{ medic.quantidade }}</div>
                                        <div class="text-muted">{{ medic.st_posologia }}</div>
                                        <div class="small text-muted" v-if="medic.st_ciclo_dias">{{ medic.st_ciclo_dias }}</div>
                                        <div class="small text-muted" v-if="medic.st_observacoes">{{ medic.st_observacoes }}</div>

                                        <div v-if="medic.diluidos && medic.diluidos.length" class="mt-2">
                                            <div v-for="(diluido, idx) in medic.diluidos" :key="idx" :class="diluido.block_d ?  'text-muted' : ''">
                                                <i class="fa fa-plus-circle mr-2" aria-hidden="true"></i>
                                                {{ diluido.item.search }}
                                                <span v-if="diluido.in_quantidade">
                                                    <i class="fa fa-caret-right mx-2" aria-hidden="true"></i>
                                                    <span class="text-muted">{{ diluido.in_quantidade }}{{ diluido.apresentacao ? ' ' + diluido.apresentacao.search : 'x' }}</span>
                                                    <span v-if="diluido.qtd_calc">
                                                        <i class="fa fa-caret-right mx-2" aria-hidden="true"></i>
                                                        <strong class="text-muted">{{ diluido.qtd_calc }}</strong>
                                                    </span>
                                                </span>

                                                <span class="text-muted" v-if="diluido.st_ciclo_dias">
                                                    <i class="fa fa-caret-right mx-2" aria-hidden="true"></i>
                                                    {{ diluido.st_ciclo_dias }}
                                                </span>

                                                <span v-if="1==0 && diluido.st_ciclo_dias">
                                                    <i class="fa fa-caret-right mx-2" aria-hidden="true"></i>
                                                    <span class="text-muted">{{ diluido.st_ciclo_dias }}</span>
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="col col-12 col-md-4">
                                        <div v-if="medic.js_prescricao_horarios">
                                            <div class="row no-gutters">
                                                <div class="col col-12" :class="tem_duracao(item, horario.hora) ? '' : 'col-md-6'" v-for="(horario, ihx) in medic.js_prescricao_horarios.horarios" :key="'M' + imx + '.H' + ihx">
                                                    <az-input
                                                        sizing="sm"
                                                        class="mr-1"
                                                        :type="tem_duracao(item, horario.hora) ? 'datetime-local' : 'time'"
                                                        required
                                                        :validate="validate"
                                                        v-model="horario.hora">

                                                        <template v-slot:button-pre>
                                                            <div class="input-group-prepend">
                                                                <button class="btn btn-outline-danger" type="button" id="button-addon2" 
                                                                    @click="medic.js_prescricao_horarios.horarios.splice(ihx, 1)">
                                                                    <i class="fa fa-minus" aria-hidden="true"></i>
                                                                </button>
                                                            </div>
                                                        </template>

                                                        <template v-slot:buttonposb>

                                                            <div class="input-group-append" v-if="horario.hora">
                                                                <button class="btn btn-outline-secondary dropdown-toggle" type="button" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
                                                                    <i class="fa fa-plus" aria-hidden="true"></i>
                                                                </button>
                                                                <div class="dropdown-menu">
                                                                    <a class="dropdown-item" href="#" @click.prevent="replicar_horarios(medic, horario, 2)">2/2h</a>
                                                                    <a class="dropdown-item" href="#" @click.prevent="replicar_horarios(medic, horario, 3)">3/3h</a>
                                                                    <a class="dropdown-item" href="#" @click.prevent="replicar_horarios(medic, horario, 4)">4/4h</a>
                                                                    <a class="dropdown-item" href="#" @click.prevent="replicar_horarios(medic, horario, 6)">6/6h</a>
                                                                    <a class="dropdown-item" href="#" @click.prevent="replicar_horarios(medic, horario, 8)">8/8h</a>
                                                                    <a class="dropdown-item" href="#" @click.prevent="replicar_horarios(medic, horario, 12)">12/12h</a>
                                                                </div>
                                                            </div>
                                                        </template>

                                                    </az-input>
                                                </div>
                                            </div>
                                        </div>

                                        <div class="btn-group" role="group" aria-label="Basic example" v-if="!medic.block_d">
                                            <div class="dropdown">
                                                <button class="btn btn-primary btn-sm dropdown-toggle" type="button" id="dropdownMenuButton" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
                                                    {{ qtde }}
                                                </button>
                                                <div class="dropdown-menu" aria-labelledby="dropdownMenuButton">
                                                    <a class="dropdown-item" href="#" v-for="qtd in 6" @click.prevent="qtde = qtd" :key="qtd">
                                                        {{ qtd }}
                                                    </a>
                                                </div>
                                            </div>
                                            <button type="button" class="btn btn-primary btn-sm" @click="incluir_horario(medic)">
                                                <i class="fa fa-plus" aria-hidden="true"></i>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                <hr>
                            </tr>
                        </table>

                        <br><br><br><br>

                        <pre v-if="false" class="alert alert-warning mt-3">{{ item }}</pre>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-secondary" data-dismiss="modal">Fechar</button>
                        <az-submit @click="salvar_aprazamento()">Atualizar</az-submit>
                    </div>
                </div>
            </div>
        </div>
        -->

        <aprazamento-modal ref="aprazamento" @refresh="load()" />
        <!-- @solicitar="pos_inclusao_prescricao" :pode_solicitar="true"  -->

    </div>
</template>

<script>
import _ from 'lodash';
import AzInput from '../../shared/forms/AzInput.vue';
import AzEmptyState from '../../util/AzEmptyState.vue';
import CelulaDadosPaciente from '../../shared/table/CelulaDadosPaciente.vue';
import CelulaDadosProcedimento from '../../shared/table/CelulaDadosProcedimento.vue';
import moment from 'moment';
import AprazamentoModal from './AprazamentoModal.vue';

export default {
    data () { 
        return { 
            referencia: this.$util.hoje(), 
            search: "", 
            dados: null, 
            item: null, 
            qtde: 1, 
            validate: false, 
            filtro: { 
                id_unidade_operacao: this.$util.getCookie('id_unidade_operacao'),
                id_fila_laudo: "", 
                id_medico: "", 
                paciente: "", 
                id_centro_custo: "", 
            },
            filas: [], 
        }
    },

    components: { 
        AzInput, 
        AzEmptyState, 
        CelulaDadosPaciente,
        CelulaDadosProcedimento, 
        AprazamentoModal, 
    },

    computed: { 
        is_fila_intern: function () { 
            return this.fila_selected && this.fila_selected.fl_fila_intern ? true : false;
        },

        fila_selected() { 
            return this.filas.find(f => f.id_fila_laudo == this.filtro.id_fila_laudo);
        },

        lista: function () { 
            if ( !this.dados )
                return [];

            return this.dados.lista;
        }
    },

    methods: { 
        init () { 
            // if ( )
        },

        quando (data) { 
            if ( data && data.slice(0,10) == this.$util.hoje() ) { 
                return 'Hoje'
            } else { 
                return this.$util.getFormatDate(data, 'DD/MM');
            }
        },  
        
        cor_contato (receita) { 
            if ( receita && receita.contato && receita.valid_id_marcacao && receita.contato.id_marcacao != receita.valid_id_marcacao ) { 
                return 'light text-muted';
            }

            return ( receita && receita.contato && receita.contato.contato_status ) ? receita.contato.contato_status.st_color : 'secondary';
        },
        
        replicar_horarios (medic, horario, intervalo) { 

            let qtde = ( 24 / intervalo ) - 1;
            let base = horario.hora.slice(-5); 
            let data = horario.hora.length > 5 ? horario.hora.slice(0, 10) : this.$util.hoje();

            if ( horario.hora.length == 5 ) { 
                horario.hora = data + ' ' + horario.hora;
            }

            for (let index = 0; index < qtde; index++) {
                let nova_hora = moment(base, 'HH:mm').add(intervalo * (index + 1), 'hours').format('HH:mm');

                if ( nova_hora < base ) { 
                    nova_hora = moment(data, 'YYYY-MM-DD').add(1, 'days').format('YYYY-MM-DD') + ' ' + nova_hora;
                } else { 
                    nova_hora = data + ' ' + nova_hora;
                }

                if ( !medic.js_prescricao_horarios.horarios.find(h => h.hora == nova_hora) ) { 
                    medic.js_prescricao_horarios.horarios.push({ 
                        hora: nova_hora,
                        status: "A", 
                        obs: "",
                        responsavel_atualizacao: [{
                            st_nome: this.$store.state.user.pessoa.st_nome, 
                            id_pessoa: this.$store.state.user.pessoa.id_pessoa,
                            dt_atualizacao: moment().format('YYYY-MM-DD HH:mm:ss')
                        }]
                    });
                }
            }

        },

        tem_duracao (receita, hora = '') { 
            if ( hora && hora.length <= 5 ) { 
                return false;
            }

            if ( hora && hora.length > 10 ) { 
                return true;
            }

            if ( receita.in_duracao > 1 && _.get(receita, 'anamnese.atendimento_procedimento.atendimento.id_tipo_atendimento') == 7 ) { 
                return true;
            }

            return false;
        },

        aprazados (receita) { 
            return receita.items.find(item => item.js_prescricao_horarios && item.js_prescricao_horarios.horarios.length) ? true : false;
        },

        incluir_horario (medic) { 
            if ( !medic.js_prescricao_horarios ) { 
                medic.js_prescricao_horarios = { 
                    horarios: [],
                };
            }

            for (let index = 0; index < this.qtde; index++) {
                
                medic.js_prescricao_horarios.horarios.push({ 
                    hora: "",
                    status: "A", 
                    obs: "",
                    responsavel_atualizacao: [{
                        st_nome: this.$store.state.user.pessoa.st_nome, 
                        id_pessoa: this.$store.state.user.pessoa.id_pessoa,
                        dt_atualizacao: moment().format('YYYY-MM-DD HH:mm:ss')
                    }]
                });

            }
        },

        aprazar (receita) { 
            this.$refs.aprazamento.aprazar(receita);
        
            /*
            this.item = _.cloneDeep(receita);

            this.$nextTick(() => { 
                $('#aprazamentoModal').modal('show');
            });
            */
        },

        tratar_paciente (atendimento) { 
            return { 
                marcacao: atendimento
            };
        },

        tratar_procedimento (receita) { 
            return { 
                marcacao: { 
                    procedimento: receita.anamnese.atendimento_procedimento.procedimento,
                    medico: receita.anamnese.atendimento_procedimento.atendimento.medico, 
                }
            };
        },

        salvar_aprazamento () { 
            this.$util.atualizar(this, { 
                service: 'enfermagem',
                func: 'aprazar',
                dados: this.item,
                modal: 'aprazamentoModal',
                reload: true, 
            });
        },

        load() { 
            this.$util.load(this, { 
                service: 'enfermagem',
                func: 'aprazamentos',
                parms: { 
                    referencia: this.referencia, 
                    lista_dias: true, 
                    ...this.filtro, 
                }, 
                loadTo: res => { 
                    this.dados = res; 
                }
            });
        }
    },

    beforeMount() { 
        if ( this.$store.state.user.assist_id_medico ) { 
            
            this.filtro.id_medico = this.$store.state.user.assist_id_medico;

        } else if ( this.$store.state.user.medico 
            && this.$store.state.user.medico.conselho 
            && this.$store.state.user.medico.conselho.st_sigla != 'COREN' ) { 

            this.filtro.id_medico = this.$store.state.user.medico.id_pessoa;

        }

        this.filtro.id_fila_laudo = this.$util.getCookie('enfermagem-fila');
    },

    mounted() { 
        this.$store.commit("setBreadcrumb", [
            { name: "Assistência", path: "/assistencia/atend" },
            { name: "Aprazamento", path: "/assistencia/apraz" },
        ]);

        this.load();

        let parms = { 
            enfermagem: true, 
        };

        this.$util.loadItem(this, { 
            service: 'fila_laudos',
            func: 'index',
            parms: parms
        })
        .then(res => { 
            this.filas = res

            if ( res && res.length == 1 ) { 
                this.filtro.id_fila_laudo = res[0].id_fila_laudo;
            }
        });
    }
}
</script>